/**
 * Word-search generation from the learner's own vocabulary.
 *
 * A Verbi reviewer singled this out: "I just did a crossword (that it generated based on
 * the new words it introduced to me)… Looks like an app that is ACTUALLY WORTH PAYING
 * FOR." Games built from *your* items are a retention feature worth copying, and because
 * they are pure generation they stay free forever (docs/08-monetization.md §3).
 *
 * Deterministic: seeded by a string, so the same learner + day produces the same puzzle
 * across devices and the output is testable.
 */

export type Direction = 'E' | 'S' | 'SE' | 'NE' | 'W' | 'N' | 'NW' | 'SW';

export interface PlacedWord {
  readonly word: string;
  readonly itemId: string;
  readonly row: number;
  readonly col: number;
  readonly direction: Direction;
}

export interface WordSearch {
  readonly grid: readonly string[][];
  readonly placed: readonly PlacedWord[];
  /** Words that did not fit; surfaced so the caller can log it rather than silently drop. */
  readonly unplaced: readonly string[];
  readonly size: number;
}

export interface WordSearchInput {
  readonly words: readonly { itemId: string; text: string }[];
  readonly size?: number;
  readonly seed: string;
  /** Allow reversed and diagonal placements. Off for beginner levels. */
  readonly difficulty?: 'easy' | 'normal' | 'hard';
}

const DIRECTION_SETS: Readonly<Record<'easy' | 'normal' | 'hard', readonly Direction[]>> = {
  easy: ['E', 'S'],
  normal: ['E', 'S', 'SE', 'NE'],
  hard: ['E', 'S', 'SE', 'NE', 'W', 'N', 'NW', 'SW'],
};

const DELTAS: Readonly<Record<Direction, readonly [number, number]>> = {
  E: [0, 1],
  W: [0, -1],
  S: [1, 0],
  N: [-1, 0],
  SE: [1, 1],
  NE: [-1, 1],
  SW: [1, -1],
  NW: [-1, -1],
};

/** Small deterministic PRNG (mulberry32) seeded from a string. */
export function createRng(seed: string): () => number {
  let h = 1779033703 ^ seed.length;
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(h ^ seed.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  let a = h >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Normalizes a word for grid placement: uppercase, accents stripped, non-letters removed.
 * The *display* word keeps its accents; only the grid is folded, because hunting for
 * "CAÑON" in a grid of unaccented letters is a typography puzzle, not a language one.
 */
export function gridForm(word: string): string {
  return word
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z]/g, '');
}

/** Alphabet used for filler letters, per script family. */
function fillerAlphabet(words: readonly string[]): string {
  const letters = new Set<string>();
  for (const w of words) for (const ch of w) letters.add(ch);
  // Bias filler toward letters already present so the puzzle looks coherent.
  const base = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  return [...letters].join('') + base;
}

export function generateWordSearch(input: WordSearchInput): WordSearch {
  const rng = createRng(input.seed);
  const difficulty = input.difficulty ?? 'normal';
  const directions = DIRECTION_SETS[difficulty];

  const prepared = input.words
    .map((w) => ({ ...w, grid: gridForm(w.text) }))
    .filter((w) => w.grid.length >= 3)
    // Longest first: long words are hardest to place, so place them while space is free.
    .sort((a, b) => b.grid.length - a.grid.length || a.itemId.localeCompare(b.itemId));

  const longest = prepared[0]?.grid.length ?? 5;
  const size = Math.max(input.size ?? 0, longest + 2, 8);

  const grid: (string | null)[][] = Array.from({ length: size }, () =>
    new Array<string | null>(size).fill(null),
  );

  const placed: PlacedWord[] = [];
  const unplaced: string[] = [];

  for (const word of prepared) {
    const spot = findSpot(grid, word.grid, directions, rng, size);
    if (spot === null) {
      unplaced.push(word.text);
      continue;
    }
    write(grid, word.grid, spot.row, spot.col, spot.direction);
    placed.push({
      word: word.text,
      itemId: word.itemId,
      row: spot.row,
      col: spot.col,
      direction: spot.direction,
    });
  }

  const alphabet = fillerAlphabet(prepared.map((p) => p.grid));
  const filled: string[][] = grid.map((row) =>
    row.map((cell) => cell ?? alphabet[Math.floor(rng() * alphabet.length)] ?? 'E'),
  );

  return { grid: filled, placed, unplaced, size };
}

interface Spot {
  readonly row: number;
  readonly col: number;
  readonly direction: Direction;
}

function findSpot(
  grid: readonly (string | null)[][],
  word: string,
  directions: readonly Direction[],
  rng: () => number,
  size: number,
): Spot | null {
  const attempts = size * size * directions.length;
  for (let i = 0; i < attempts; i++) {
    const direction = directions[Math.floor(rng() * directions.length)] as Direction;
    const row = Math.floor(rng() * size);
    const col = Math.floor(rng() * size);
    if (fits(grid, word, row, col, direction, size)) return { row, col, direction };
  }

  // Deterministic exhaustive fallback, so a placeable word is never dropped by bad luck.
  for (const direction of directions) {
    for (let row = 0; row < size; row++) {
      for (let col = 0; col < size; col++) {
        if (fits(grid, word, row, col, direction, size)) return { row, col, direction };
      }
    }
  }
  return null;
}

function fits(
  grid: readonly (string | null)[][],
  word: string,
  row: number,
  col: number,
  direction: Direction,
  size: number,
): boolean {
  const [dr, dc] = DELTAS[direction];
  const endRow = row + dr * (word.length - 1);
  const endCol = col + dc * (word.length - 1);
  if (endRow < 0 || endRow >= size || endCol < 0 || endCol >= size) return false;

  for (let i = 0; i < word.length; i++) {
    const r = row + dr * i;
    const c = col + dc * i;
    const existing = grid[r]?.[c];
    if (existing !== null && existing !== undefined && existing !== word[i]) return false;
  }
  return true;
}

function write(
  grid: (string | null)[][],
  word: string,
  row: number,
  col: number,
  direction: Direction,
): void {
  const [dr, dc] = DELTAS[direction];
  for (let i = 0; i < word.length; i++) {
    const r = row + dr * i;
    const c = col + dc * i;
    const line = grid[r];
    if (line !== undefined) line[c] = word[i] as string;
  }
}
